import { ImagePlus, RefreshCw } from "lucide-react";
import { ChangeEvent, useRef, useState } from "react";

import { useUploadVenueLogo } from "@/features/venues/model/useVenueProfile";
import type { VenueProfile } from "@/features/venues/types";
import {
  ALLOWED_IMAGE_TYPES,
  IMAGE_ACCEPT_ATTR,
  validateUpload,
} from "@/shared/lib/uploadValidation";

import { StepFooter } from "@/features/venue-onboarding/ui/StepFooter";

interface Props {
  profile: VenueProfile;
  onSaved: () => void;
  onBack?: () => void;
}

function extractErrorMessage(err: unknown): string {
  // Pull the FastAPI-shaped detail out of the Axios error so the venue sees
  // why the upload was rejected (wrong type, too big, contents mismatch, etc.)
  // instead of a generic "failed". Falls back to the Axios message and finally
  // a generic string so we never render `undefined`.
  if (err && typeof err === "object") {
    const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
    if (typeof detail === "string" && detail) return detail;
    const message = (err as { message?: unknown }).message;
    if (typeof message === "string" && message) return message;
  }
  return "Upload failed.";
}

export function LogoStep({ profile, onSaved, onBack }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const upload = useUploadVenueLogo();
  const [clientError, setClientError] = useState<string | null>(null);
  const [logoLoadFailed, setLogoLoadFailed] = useState(false);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    const err = validateUpload(file, ALLOWED_IMAGE_TYPES);
    if (err) {
      setClientError(err);
      return;
    }
    setClientError(null);
    setLogoLoadFailed(false);
    upload.mutate(file);
  };

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSaved();
  };

  const hasLogo = !!profile.logo_url;

  return (
    <form onSubmit={onSubmit} className="space-y-5">
      <div>
        <h2 className="text-xl font-semibold tracking-display-tight text-copy">
          Add your logo
        </h2>
        <p className="mt-1 text-sm text-copy-muted">
          Workers see this on every shift card. A real logo or storefront photo gets more
          applications — but it's optional, you can add one later from your profile.
        </p>
      </div>

      <div className="flex flex-col items-center gap-4 rounded-lg border border-border bg-surface-sunken p-6 sm:flex-row sm:items-start">
        <div className="flex h-28 w-28 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-border bg-surface">
          {profile.logo_url && !logoLoadFailed ? (
            <img
              key={profile.logo_url}
              src={profile.logo_url}
              alt={profile.business_name || "Venue logo"}
              className="h-full w-full object-cover"
              onError={() => setLogoLoadFailed(true)}
            />
          ) : (
            <ImagePlus className="h-8 w-8 text-copy-subtle" aria-hidden />
          )}
        </div>
        <div className="flex-1 space-y-2 text-center sm:text-left">
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={upload.isPending}
            className="inline-flex items-center gap-2 rounded-md border border-border bg-surface px-3 py-2 text-sm hover:bg-surface-sunken disabled:opacity-50"
          >
            {upload.isPending ? (
              <>
                <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                Uploading…
              </>
            ) : hasLogo ? (
              <>
                <RefreshCw className="h-3.5 w-3.5" />
                Replace logo
              </>
            ) : (
              <>
                <ImagePlus className="h-3.5 w-3.5" />
                Upload logo
              </>
            )}
          </button>
          <p className="text-xs text-copy-muted">
            JPG, PNG, WebP, GIF, BMP, or HEIC. Max 5 MB. Square images look best.
          </p>
          {clientError && <p className="text-xs text-destructive-foreground">{clientError}</p>}
          {upload.isError && (
            <p className="text-xs text-destructive-foreground">
              {extractErrorMessage(upload.error)}
            </p>
          )}
        </div>
        <input
          ref={inputRef}
          type="file"
          accept={IMAGE_ACCEPT_ATTR}
          className="hidden"
          onChange={handleChange}
        />
      </div>

      <StepFooter
        onBack={onBack}
        isSubmitting={false}
        nextLabel={hasLogo ? "Continue" : "Skip for now"}
      />
    </form>
  );
}
